import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, Voicemail } from "lucide-react";
import { useState } from "react";
import { PhoneLink } from "@/components/PhoneLink";
import { useToast } from "@/components/Toast";
import { Button, Empty, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { formatDateTime, formatPhone } from "@/lib/format";
import {
  formatDuration,
  KIND_LABEL,
  telephony,
  voiceCallsUrl,
  type TelephonyEventRow,
} from "@/lib/telephony";

const ICON = {
  call_in: PhoneIncoming,
  call_out: PhoneOutgoing,
  missed: PhoneMissed,
  voicemail: Voicemail,
  sms_in: MessageSquare,
  sms_out: MessageSquare,
} as const;

/**
 * v2.2 P4.5: the Calls tab on a lead and the telephony half of a deal's Outreach tab.
 *
 * Newest first: kind, time, duration, the transcript or summary, a player for a stored voicemail (a
 * signed URL, fetched on Play), and "Open in Voice". A voicemail's AI summary is a PENDING suggestion:
 * "Use as next action" is a person's click, and is the only way it changes anything (Rule 0).
 */
export function CallsPanel({ leadId, dealId }: { leadId?: string; dealId?: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["telephony", leadId ?? null, dealId ?? null],
    queryFn: () => telephony.events({ lead_id: leadId, deal_id: dealId }),
  });
  if (isLoading) return <Spinner label="Loading calls…" />;
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <Empty
        title="No calls, texts or voicemails yet."
        hint="Google Voice activity for this number appears here within one poll of it happening."
      />
    );
  }
  return (
    <ul className="space-y-2" data-testid="calls-list">
      {items.map((ev) => (
        <CallRow key={ev.id} ev={ev} leadId={leadId} />
      ))}
    </ul>
  );
}

function CallRow({ ev, leadId }: { ev: TelephonyEventRow; leadId?: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [audio, setAudio] = useState<string | null>(null);
  const Icon = ICON[ev.kind as keyof typeof ICON] ?? Phone;
  const inbound = ["call_in", "missed", "voicemail", "sms_in"].includes(ev.kind);
  // v2.10 P1.3: the party that is not ours (the linked cell is ours too); "via" is always the Voice line.
  const other = ev.counterparty_e164 ?? (inbound ? ev.from_e164 : ev.to_e164);
  const suggestion = ev.suggestion as
    | { recommended_next_action?: { text?: string; due_offset_days?: number }; reply_class?: string }
    | null
    | undefined;

  const play = useMutation({
    mutationFn: () => telephony.recording(ev.id),
    onSuccess: (r) => setAudio(r.url),
    onError: (e: Error) => toast.push(e.message, "danger"),
  });
  const useAction = useMutation({
    mutationFn: () => {
      const next = suggestion?.recommended_next_action;
      const due = new Date();
      due.setDate(due.getDate() + (next?.due_offset_days ?? 0));
      return api.patchLead(leadId!, { next_action: next?.text ?? "", next_action_due: due.toISOString() });
    },
    onSuccess: () => {
      toast.push("Next action set from the voicemail");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <li className="rounded border px-2 py-1.5 text-xs" data-testid="call-row" data-kind={ev.kind}>
      <div className="flex flex-wrap items-center gap-2">
        <Icon
          className={ev.kind === "missed" ? "size-4 text-danger" : "size-4 text-fg-muted"}
          aria-label={KIND_LABEL[ev.kind] ?? ev.kind}
        />
        <span className="font-medium">{KIND_LABEL[ev.kind] ?? ev.kind}</span>
        <PhoneLink e164={other} leadId={ev.lead_id} dealId={ev.cv_deal_id} surface="calls-panel" />
        {ev.via_e164 && (
          <span className="text-[10px] text-fg-muted" data-testid="call-via">
            via {formatPhone(ev.via_e164)}
          </span>
        )}
        <span className="text-fg-muted">{formatDateTime(ev.occurred_at)}</span>
        <span className="tabular-nums text-fg-muted">{formatDuration(ev.duration_s)}</span>
        <span className="ml-auto flex items-center gap-1">
          {ev.has_recording && !audio && (
            <Button size="sm" variant="ghost" onClick={() => play.mutate()} data-testid="call-play">
              Play
            </Button>
          )}
          <a
            href={voiceCallsUrl()}
            target="cornerstone-voice"
            className="rounded px-1.5 py-0.5 text-fg-muted hover:bg-surface-2 hover:text-fg"
            data-testid="call-open-voice"
          >
            Open in Voice
          </a>
        </span>
      </div>
      {audio && <audio controls src={audio} className="mt-1 h-8 w-full" data-testid="call-audio" />}
      {(ev.body || ev.transcript) && (
        <p className="mt-1 whitespace-pre-wrap" data-testid="call-text">
          {ev.body ?? ev.transcript}
        </p>
      )}
      {ev.summary && (
        <div className="mt-1 rounded bg-surface-2 px-2 py-1" data-testid="call-summary">
          <p className="text-[10px] font-medium uppercase tracking-wide text-fg-muted">
            Suggested — pending your review
          </p>
          <p className="whitespace-pre-wrap">{ev.summary}</p>
          {leadId && suggestion?.recommended_next_action?.text && (
            <Button
              size="sm"
              className="mt-1"
              onClick={() => useAction.mutate()}
              disabled={useAction.isPending}
              data-testid="call-use-action"
            >
              Use as next action: {suggestion.recommended_next_action.text}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
